import type { ChatbotBotPublic } from '../../types/chatbot.types';

interface BotSelectorProps {
  bots: ChatbotBotPublic[];
  onSelectBot: (botId: string) => void;
  isLoading: boolean;
  onLogout?: () => void;
}

function BotIcon({ iconUrl, name }: { iconUrl: string | null; name: string }) {
  return (
    <div className='relative'>
      {/* Glow effect */}
      <div className='absolute inset-0 animate-pulse-slow rounded-full bg-gradient-to-r from-primary to-primary-700 blur-lg opacity-40' />
      {iconUrl ? (
        <img
          src={iconUrl}
          alt={name}
          className='relative h-16 w-16 rounded-full border-2 border-white object-cover shadow-lg'
        />
      ) : (
        <div className='relative flex h-16 w-16 items-center justify-center rounded-full border-2 border-white bg-gradient-to-br from-primary to-primary-700 shadow-lg'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
            strokeWidth={1.5}
            stroke='currentColor'
            className='h-8 w-8 text-white'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z'
            />
          </svg>
        </div>
      )}
    </div>
  );
}

function BotCardSkeleton() {
  return (
    <div className='group relative overflow-hidden rounded-2xl border-2 border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800'>
      <div className='flex items-start gap-4'>
        <div className='h-16 w-16 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700' />
        <div className='flex-1 space-y-3'>
          <div className='h-6 w-32 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-700' />
          <div className='h-4 w-full animate-pulse rounded-xl bg-slate-100 dark:bg-slate-700/50' />
          <div className='h-4 w-3/4 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-700/50' />
        </div>
      </div>
    </div>
  );
}

export function BotSelector({ bots, onSelectBot, isLoading, onLogout }: BotSelectorProps) {
  if (isLoading) {
    return (
      <div className='relative min-h-screen bg-gradient-to-br from-background via-card to-primary-50 dark:from-background dark:via-card dark:to-background p-4'>
        {/* Animated background blobs */}
        <div className='pointer-events-none fixed inset-0 overflow-hidden'>
          <div className='absolute -top-40 -right-40 h-96 w-96 animate-pulse-slow rounded-full bg-violet-400/10 blur-3xl' />
          <div className='absolute -bottom-40 -left-40 h-96 w-96 animate-pulse-slow rounded-full bg-cyan-400/10 blur-3xl [animation-delay:1s]' />
        </div>

        <div className='relative z-10 mx-auto max-w-5xl pt-8'>
          <div className='text-center mb-8'>
            <h2 className='text-3xl font-bold text-slate-900 dark:text-white'>
              Asistentes Disponibles
            </h2>
            <p className='mt-2 text-slate-600 dark:text-slate-400'>Cargando asistentes...</p>
          </div>
          <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
            {[1, 2, 3].map(i => (
              <BotCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (bots.length === 0) {
    return (
      <div className='relative flex min-h-screen items-center justify-center overflow-hidden p-4 bg-gradient-to-br from-background via-card to-primary-50 dark:from-background dark:via-card dark:to-background'>
        {/* Animated background blobs */}
        <div className='pointer-events-none absolute inset-0 overflow-hidden'>
          <div className='absolute -top-40 -right-40 h-96 w-96 animate-pulse-slow rounded-full bg-violet-400/10 blur-3xl' />
          <div className='absolute -bottom-40 -left-40 h-96 w-96 animate-pulse-slow rounded-full bg-cyan-400/10 blur-3xl [animation-delay:1s]' />
        </div>

        <div className='relative z-10 text-center max-w-md'>
          <div className='relative mx-auto mb-6 inline-flex'>
            <div className='absolute inset-0 animate-pulse-slow rounded-3xl bg-gradient-to-r from-primary to-primary-700 blur-xl opacity-40' />
            <div className='relative flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 shadow-2xl'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={1.5}
                stroke='currentColor'
                className='h-10 w-10 text-slate-400'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z'
                />
              </svg>
            </div>
          </div>
          <h3 className='text-2xl font-bold text-slate-900 dark:text-white'>
            No hay asistentes disponibles
          </h3>
          <p className='mt-3 text-slate-600 dark:text-slate-400'>
            Vuelve más tarde o contacta al administrador.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='relative min-h-screen bg-gradient-to-br from-background via-card to-primary-50 dark:from-background dark:via-card dark:to-background'>
      {/* Animated background blobs */}
      <div className='pointer-events-none fixed inset-0 overflow-hidden'>
        <div className='absolute -top-40 -right-40 h-96 w-96 animate-pulse-slow rounded-full bg-violet-400/10 blur-3xl' />
        <div className='absolute -bottom-40 -left-40 h-96 w-96 animate-pulse-slow rounded-full bg-cyan-400/10 blur-3xl [animation-delay:1s]' />
      </div>

      <div className='relative z-10 mx-auto max-w-5xl p-4 pt-8'>
        {onLogout && (
          <div className='mb-4 flex justify-end'>
            <button
              type='button'
              onClick={onLogout}
              className='inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm backdrop-blur transition-all hover:border-red-200 hover:bg-red-50 hover:text-red-600 active:scale-[0.98] dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-300 dark:hover:border-red-800 dark:hover:bg-red-950/40 dark:hover:text-red-300'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={1.8}
                stroke='currentColor'
                className='h-4 w-4'
                aria-hidden='true'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6A2.25 2.25 0 005.25 5.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9'
                />
              </svg>
              Cerrar sesión
            </button>
          </div>
        )}

        {/* Header */}
        <div className='text-center mb-10 animate-in fade-in-0 slide-in-from-top-4 duration-500'>
          <span className='inline-block rounded-full border border-violet-200 bg-violet-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-violet-700 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-300 mb-4'>
            Selecciona un asistente
          </span>
          <h2 className='text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white'>
            ¿En qué podemos ayudarte hoy?
          </h2>
          <p className='mt-3 text-slate-600 dark:text-slate-400 max-w-lg mx-auto'>
            Elige uno de nuestros asistentes virtuales para comenzar.
          </p>
        </div>

        {/* Bot cards grid - CLEARLY CLICKABLE */}
        <div className='grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
          {bots.map((bot, index) => (
            <button
              key={bot.id}
              onClick={() => onSelectBot(bot.id)}
              className='group relative text-left animate-in fade-in-0 slide-in-from-bottom-8 duration-500'
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className='relative h-full rounded-2xl border-2 border-slate-200 bg-white p-5 shadow-sm transition-all duration-200 hover:border-violet-400 hover:shadow-lg hover:-translate-y-1 active:scale-[0.98] dark:border-slate-700 dark:bg-slate-800 dark:hover:border-violet-600'>
                {/* Badge on hover */}
                <div className='absolute -top-2 -right-2 rounded-full bg-violet-500 px-3 py-1 text-xs font-semibold text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100'>
                  Seleccionar
                </div>

                <div className='flex items-start gap-4'>
                  <BotIcon iconUrl={bot.iconUrl} name={bot.name} />
                  <div className='flex-1 space-y-2'>
                    <h3 className='font-bold text-lg leading-tight text-slate-900 group-hover:text-primary dark:text-white dark:group-hover:text-violet-400 transition-colors'>
                      {bot.name}
                    </h3>
                    {bot.kind === 'CONCIERGE' && (
                      <span className='inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700 dark:bg-violet-900/40 dark:text-violet-200'>
                        Conserje
                      </span>
                    )}
                    {bot.description && (
                      <p className='text-sm leading-relaxed text-slate-600 dark:text-slate-400 line-clamp-2'>
                        {bot.description}
                      </p>
                    )}
                    {bot.conversationsCount !== undefined && bot.conversationsCount > 0 && (
                      <span className='inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                          strokeWidth={2}
                          stroke='currentColor'
                          className='h-3 w-3'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            d='M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z'
                          />
                        </svg>
                        {bot.conversationsCount} conversación
                        {bot.conversationsCount !== 1 ? 'es' : ''}
                      </span>
                    )}
                  </div>
                </div>

                {/* Arrow indicator */}
                <div
                  className='absolute bottom-5 right-5 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-primary to-primary-700 text-white opacity-0 transition-all duration-200 group-hover:opacity-100'
                  aria-hidden='true'
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={2.5}
                    stroke='currentColor'
                    className='h-4 w-4'
                    aria-hidden='true'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3'
                    />
                  </svg>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
